import React, { useState, useEffect, useCallback } from 'react';
import { AY_ADLARI } from '../../constants/tarih';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import useUrlState from '../../hooks/useUrlState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const DURUM_RENK = {
  TAMAMLANDI:  { bg: '#e8f5e9', fg: '#2e7d32', label: 'Tamamlandı' },
  TASLAK:      { bg: '#fff8e1', fg: '#f57f17', label: 'Taslak' },
  IPTAL:       { bg: '#fce4ec', fg: '#c62828', label: 'İptal' },
  GERI_ALINDI: { bg: '#ede7f6', fg: '#5e35b1', label: 'Geri Alındı' },
};

const durumRozeti = (durum) => {
  const d = DURUM_RENK[durum] || { bg: '#f5f5f5', fg: '#666', label: durum };
  return (
    <span style={{
      background: d.bg, color: d.fg,
      fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 12,
    }}>
      {d.label}
    </span>
  );
};

const fmtTarih = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtMiktar = (v) => {
  if (v == null) return '—';
  return Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 3 });
};

const farkRenk = (fark) => {
  if (Math.abs(fark) < 0.001) return { color: '#666' };
  return { color: fark > 0 ? '#2e7d32' : '#c62828', fontWeight: 700 };
};


export default function StokSayimGecmisPage() {
  const { token, user } = useAuth();
  const bugun = new Date();
  const [geriAliniyor, setGeriAliniyor] = useState(null);

  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [sayimlar, setSayimlar] = useState([]);
  const [yukluyor, setYukluyor] = useState(false);
  const [hata, setHata] = useState(null);

  // Filtreler URL'e bağlı: sayım detayına gidip / başka sayfaya gidip geri dönünce korunur.
  const [filtreLokasyon, setFiltreLokasyon] = useUrlState('lokasyon', '');
  const [filtreDurum, setFiltreDurum] = useUrlState('durum', '');
  const [filtreYil, setFiltreYil] = useUrlState('yil', String(bugun.getFullYear()));
  const [filtreAy, setFiltreAy] = useUrlState('ay', '');

  const [acikDetay, setAcikDetay] = useState(null);
  const [detayData, setDetayData] = useState({});
  const [detayYukluyor, setDetayYukluyor] = useState(false);

  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${token || localStorage.getItem('access_token')}`,
  }), [token]);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setLokasyonlar(Array.isArray(data) ? data.filter(l => l.aktif) : []))
      .catch(() => {});
  }, [authHeader]);

  const yukle = useCallback(() => {
    setYukluyor(true);
    setHata(null);
    const params = new URLSearchParams();
    if (filtreLokasyon) params.set('lokasyon_id', filtreLokasyon);
    if (filtreDurum) params.set('durum', filtreDurum);
    if (filtreYil) params.set('yil', filtreYil);
    if (filtreAy) params.set('ay', filtreAy);

    fetch(`${API_URL}/api/stok-sayim/oturumlar?${params}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { setSayimlar(Array.isArray(data) ? data : []); })
      .catch(() => setHata('Sayım geçmişi yüklenemedi.'))
      .finally(() => setYukluyor(false));
  }, [authHeader, filtreLokasyon, filtreDurum, filtreYil, filtreAy]);

  useEffect(() => { yukle(); }, [yukle]);

  const toggleDetay = async (sayimId) => {
    if (acikDetay === sayimId) {
      setAcikDetay(null);
      return;
    }
    setAcikDetay(sayimId);
    if (detayData[sayimId]) return;

    setDetayYukluyor(true);
    try {
      const r = await fetch(`${API_URL}/api/stok-sayim/oturum/${sayimId}`, { headers: authHeader() });
      if (r.ok) {
        const data = await r.json();
        setDetayData(prev => ({ ...prev, [sayimId]: data }));
      }
    } finally {
      setDetayYukluyor(false);
    }
  };

  const geriAl = async (e, sayimId) => {
    e.stopPropagation();
    if (!window.confirm('Bu tamamlanmış sayım geri alınacak; ürettiği stok farkları ters kayıtla nötrlenecek. Onaylıyor musunuz?')) return;
    setGeriAliniyor(sayimId);
    try {
      const r = await fetch(`${API_URL}/api/stok-sayim/oturum/${sayimId}/geri-al`, { method: 'POST', headers: authHeader() });
      if (r.ok) {
        setDetayData(prev => { const n = { ...prev }; delete n[sayimId]; return n; });
        yukle();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || 'Geri alma başarısız');
      }
    } finally {
      setGeriAliniyor(null);
    }
  };

  const yilSecenekleri = [];
  for (let y = bugun.getFullYear(); y >= bugun.getFullYear() - 3; y--) {
    yilSecenekleri.push(y);
  }

  const inputStyle = {
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    padding: '6px 10px',
    fontSize: 13,
    background: colors.surface,
    color: colors.text,
    outline: 'none',
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <Header title="Sayım Geçmişi" subtitle="Tamamlanan ve devam eden sayım oturumları" />

      {/* Filtreler */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, padding: '12px 16px',
        background: colors.surface, borderBottom: `1px solid ${colors.border}`,
        alignItems: 'center',
      }}>
        <select value={filtreYil} onChange={e => setFiltreYil(e.target.value)} style={inputStyle}>
          {yilSecenekleri.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={filtreAy} onChange={e => setFiltreAy(e.target.value)} style={inputStyle}>
          <option value="">Tüm Aylar</option>
          {AY_ADLARI.map((ad, i) => <option key={i + 1} value={i + 1}>{ad}</option>)}
        </select>

        <select value={filtreLokasyon} onChange={e => setFiltreLokasyon(e.target.value)} style={inputStyle}>
          <option value="">Tüm Lokasyonlar</option>
          {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
        </select>

        <select value={filtreDurum} onChange={e => setFiltreDurum(e.target.value)} style={inputStyle}>
          <option value="">Tüm Durumlar</option>
          <option value="TAMAMLANDI">Tamamlandı</option>
          <option value="TASLAK">Taslak</option>
          <option value="IPTAL">İptal</option>
        </select>
      </div>

      {/* İçerik */}
      <div style={{ padding: '16px' }}>
        {hata && (
          <div style={{ padding: 12, background: '#fce4ec', color: '#c62828', borderRadius: radius.sm, marginBottom: 12 }}>
            {hata}
          </div>
        )}

        {yukluyor ? (
          <div style={{ textAlign: 'center', color: colors.textSecondary, padding: 40 }}>Yükleniyor…</div>
        ) : sayimlar.length === 0 ? (
          <div style={{ textAlign: 'center', color: colors.textSecondary, padding: 40 }}>
            Seçilen dönemde sayım kaydı bulunamadı.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sayimlar.map(s => (
              <div key={s.sayim_id} style={{
                background: colors.surface,
                borderRadius: radius.md,
                boxShadow: shadows.sm,
                border: `1px solid ${colors.border}`,
                overflow: 'hidden',
              }}>
                {/* Başlık satırı */}
                <div
                  onClick={() => toggleDetay(s.sayim_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', cursor: 'pointer',
                    background: acikDetay === s.sayim_id ? colors.bgHover || '#f8f9fa' : 'transparent',
                  }}
                >
                  <span style={{ fontSize: 16 }}>{acikDetay === s.sayim_id ? '▼' : '▶'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{s.lokasyon_ad}</span>
                      {durumRozeti(s.durum)}
                      {s.farkli_kalem > 0 && (
                        <span style={{
                          background: '#fff3e0', color: '#e65100',
                          fontSize: 11, fontWeight: 600,
                          padding: '2px 7px', borderRadius: 12,
                        }}>
                          {s.farkli_kalem} farklı kalem
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                      {fmtTarih(s.baslangic_tarihi)}
                      {s.bitis_tarihi && ` → ${fmtTarih(s.bitis_tarihi)}`}
                      {' · '}{s.kullanici_ad}
                      {s.aciklama && ` · ${s.aciklama}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: colors.textSecondary, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{s.toplam_kalem} kalem</span>
                    {s.durum === 'TAMAMLANDI' && ['yonetim', 'admin'].includes(user?.role) && (
                      <button
                        onClick={(e) => geriAl(e, s.sayim_id)}
                        disabled={geriAliniyor === s.sayim_id}
                        title="Tamamlanmış sayımı geri al (stok farklarını ters kayıtla nötrler)"
                        style={{ padding: '4px 10px', fontSize: 12, background: '#ede7f6', color: '#5e35b1', border: '1px solid #b39ddb', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}
                      >
                        {geriAliniyor === s.sayim_id ? '...' : 'Geri Al'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Detay tablosu */}
                {acikDetay === s.sayim_id && (
                  <div style={{ borderTop: `1px solid ${colors.border}` }}>
                    {detayYukluyor && !detayData[s.sayim_id] ? (
                      <div style={{ padding: '12px 16px', color: colors.textSecondary, fontSize: 13 }}>Yükleniyor…</div>
                    ) : detayData[s.sayim_id] ? (
                      <DetayTablosu kalemler={detayData[s.sayim_id].kalemler} />
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetayTablosu({ kalemler }) {
  const [aramaMetni, setAramaMetni] = useState('');

  const gorunenler = aramaMetni.trim()
    ? kalemler.filter(k =>
        (k.malzeme_ad || '').toLowerCase().includes(aramaMetni.toLowerCase()) ||
        (k.malzeme_kod || '').toLowerCase().includes(aramaMetni.toLowerCase())
      )
    : kalemler;

  const farklilar = gorunenler.filter(k => k.sayilan_miktar != null && Math.abs((k.sayilan_miktar || 0) - k.sistem_snapshot_miktar) > 0.001);
  const sayilmayanlar = gorunenler.filter(k => k.sayilan_miktar == null);

  const thStyle = {
    padding: '7px 10px', textAlign: 'left', fontSize: 11,
    fontWeight: 600, color: '#888', borderBottom: '1px solid #eee',
    background: '#fafafa', whiteSpace: 'nowrap',
  };
  const tdStyle = (align) => ({
    padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #f0f0f0',
    textAlign: align || 'left', verticalAlign: 'middle',
  });

  return (
    <div>
      {/* Özet */}
      <div style={{ display: 'flex', gap: 16, padding: '8px 14px', fontSize: 12, color: '#666', flexWrap: 'wrap' }}>
        <span>Toplam: <strong>{kalemler.length}</strong></span>
        <span style={{ color: '#e65100' }}>Farklı: <strong>{farklilar.length}</strong></span>
        {sayilmayanlar.length > 0 && (
          <span style={{ color: '#aaa' }}>Sayılmayan: <strong>{sayilmayanlar.length}</strong></span>
        )}
        <input
          placeholder="Malzeme ara…"
          value={aramaMetni}
          onChange={e => setAramaMetni(e.target.value)}
          style={{
            marginLeft: 'auto', border: '1px solid #ddd', borderRadius: 6,
            padding: '3px 8px', fontSize: 12, outline: 'none',
          }}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={thStyle}>Malzeme</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Sistem (snapshot)</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Sayılan</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Fark</th>
              <th style={thStyle}>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {gorunenler.map(k => {
              const fark = k.sayilan_miktar != null ? (k.sayilan_miktar - k.sistem_snapshot_miktar) : null;
              return (
                <tr key={k.malzeme_id} style={{ background: fark != null && Math.abs(fark) > 0.001 ? '#fffde7' : 'white' }}>
                  <td style={tdStyle()}>
                    <div style={{ fontWeight: 500 }}>{k.malzeme_ad}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{k.malzeme_kod} · {k.birim}</div>
                  </td>
                  <td style={tdStyle('right')}>{fmtMiktar(k.sistem_snapshot_miktar)}</td>
                  <td style={tdStyle('right')}>
                    {k.sayilan_miktar != null ? fmtMiktar(k.sayilan_miktar) : (
                      <span style={{ color: '#bbb', fontSize: 11 }}>sayılmadı</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle('right'), ...farkRenk(fark || 0) }}>
                    {fark != null ? (fark > 0 ? '+' : '') + fmtMiktar(fark) : '—'}
                  </td>
                  <td style={{ ...tdStyle(), color: '#888', fontSize: 12 }}>{k.aciklama || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
